import type { KeySignals } from './tv-focus/keys.js';
import type { EpgRow } from './epg.js';

/**
 * 숫자 키 하나 → 채널 하나 (v26, `docs/53` §3.6 · 절대 규칙 71).
 *
 * ── 왜 `tv-focus/` 가 아닌가 ─────────────────────────────────
 *
 * 그 폴더는 **포커스**의 판정이고 숫자 키는 포커스가 아니다 — 층을 옮기지도, 링을
 * 움직이지도 않는다. `keys.ts` 의 `NavIntent` 에 값을 하나 더하면 `decide.ts` 가 그것을
 * 받아 「무엇을 포커스할까」를 답해야 하는데, 답이 없다.
 *
 * **`KeySignals` 는 그쪽에서 가져온다** — 같은 이벤트에서 같은 값을 뽑으므로 모양이 둘이
 * 되면 한쪽만 수식키를 거르는 상태가 생긴다. 타입만 빌리고 코드는 더하지 않는다.
 *
 * ── 순수 함수로 뗀다 ─────────────────────────────────────────
 *
 * `KeyboardEvent` 를 직접 받지 않는다 — 이 패키지는 node 환경에서 테스트하고(jsdom 미도입),
 * 이벤트를 받는 순간 아래 판정이 **검사에서 통째로 빠진다**. 값을 읽는 일은 어댑터
 * (`use-channel-keys.ts`) 한 줄이 맡는다.
 */

/** `event.keyCode` 의 `1`~`9`. **구형 TV 브라우저는 이것만 준다** (`keys.ts` 와 같은 이유) */
const LEGACY_DIGIT_MIN = 49;
const LEGACY_DIGIT_MAX = 57;

/**
 * 이 키가 가리키는 채널 번호. **모르는 키는 `null` 이고, `null` 은 「손대지 않는다」다.**
 *
 * ── 손대지 않는 것들과 그 이유 ────────────────────────────────
 *
 * - **`0`** — 채널 번호가 아니다. `CHANNEL_NUMBER_MIN` 이 1 인 것이 그 결정이고
 *   (`docs/53` §3.2), 0번 채널을 만들 수 있게 두면 **어떤 키로도 닿지 않는 채널**이 생긴다.
 * - **수식키가 하나라도 켜져 있으면** — `Cmd+1`·`Ctrl+1` 은 **브라우저의 탭 전환**이다.
 *   삼키면 「이 앱에서만 탭 전환이 안 된다」가 되고, **맥에서만** 그렇다.
 * - **`repeat`** — 길게 누르면 전환이 연달아 일어나 리시버가 여러 번 선다. 짧게 누르는
 *   개발자는 못 본다 (`keys.ts` 가 `confirm`·`back` 에 대해 내린 그 판단).
 * - **`editable`** — 지금 이 앱에 입력 칸이 **하나도 없어 증상이 없다. 그래서 조용하다.**
 *   훗날 검색창이 생기는 날 숫자를 못 치게 된다.
 *
 * **두 자리를 받지 않는다** (절대 규칙 71). 「Enter 없이 바로」와 두 자리 버퍼는 동시에
 * 성립하지 않는다 — 마지막 키를 기다리는 순간 **1번 채널로 가는 데 1초가 걸린다.**
 * 채널이 열 개를 넘는 날 이 결정을 다시 보고, 그날 바뀌는 것은 이 함수 하나다.
 */
export function resolveChannelDigit(signals: KeySignals): number | null {
  if (signals.alt || signals.ctrl || signals.meta || signals.shift) return null;
  if (signals.repeat) return null;
  if (signals.editable) return null;

  if (signals.key.length === 1 && signals.key >= '1' && signals.key <= '9') {
    return Number(signals.key);
  }
  // `key` 가 이름을 주지 않는 구형 기기만 여기로 내려온다.
  if (signals.key === '' || signals.key === 'Unidentified') {
    const { keyCode } = signals;
    if (keyCode >= LEGACY_DIGIT_MIN && keyCode <= LEGACY_DIGIT_MAX) {
      return keyCode - LEGACY_DIGIT_MIN + 1;
    }
  }
  return null;
}

/**
 * 그 번호의 채널. **없으면 `null` 이고, 그때는 아무 일도 하지 않는다.**
 *
 * 「채널 3번이 없습니다」를 띄우지 않는다 — 데모 무대에서 그 토스트는 오조작을 **크게**
 * 보이게 만들 뿐이고, 없는 번호를 누르는 것은 실제 TV 에서도 무동작이다.
 *
 * **`rows` 는 채널 목록의 순서 그대로다**(절대 규칙 71). 여기서 정렬하지 않는다 — 찾는
 * 것은 번호이지 위치가 아니므로 순서에 기대지도 않는다.
 */
export function channelForNumber(rows: readonly EpgRow[], number: number): string | null {
  return rows.find((row) => row.channel.number === number)?.channel.channelId ?? null;
}
